import { useTranslation } from "react-i18next";
import { Package } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteInteractiveMaterial,
  getGetAllInteractiveMaterialsQueryKey,
  useGetAllInteractiveMaterials,
} from "@packages/api/madlenAPI";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";
import { InteractiveMaterialResponseDto } from "@packages/api/madlenAPI.schemas";
import { trackEvent } from "@packages/hooks/useAnalytics";
import HeroTitle from "../../components/tool_components/HeroTitle";
import ErrorComponent from "../../components/tool_components/ErrorComponent";
import Card from "../../components/card";
import DeleteConfirmPopUp from "../../components/tool_components/DeleteConfirmPopUp";
import { Text, Button } from "@core/index";
import ToolHelper from "@features/tools/components/ToolHelper";

export const InteractiveMaterialsView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>(
    undefined,
  );
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const {
    data: materials,
    isLoading,
    error,
    refetch,
  } = useGetAllInteractiveMaterials(
    {},
    {
      query: {
        refetchInterval: (query: {
          state: { data?: { data?: InteractiveMaterialResponseDto[] } };
        }) => {
          const firstItem = query.state.data?.data?.[0];
          if (!firstItem?.timestampedContent) {
            return 5000;
          }
          return false;
        },
      },
    },
  );

  const deleteInteractiveMaterial = useDeleteInteractiveMaterial({
    mutation: {
      onMutate: async ({ id }) => {
        const queryKey = getGetAllInteractiveMaterialsQueryKey();
        const previousData = queryClient.getQueryData(queryKey);

        // Optimistically update UI
        queryClient.setQueryData(queryKey, (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData?.data?.filter(
              (material: InteractiveMaterialResponseDto) => material.id !== id,
            ),
          };
        });

        return { previousData };
      },
      onError: (error, _variables, context) => {
        const queryKey = getGetAllInteractiveMaterialsQueryKey();
        if (context?.previousData) {
          queryClient.setQueryData(queryKey, context.previousData);
        }
        console.error("Failed to delete material:", error);
      },
      onSettled: () => {
        const queryKey = getGetAllInteractiveMaterialsQueryKey();
        queryClient.invalidateQueries({ queryKey });
      },
    },
  });

  const handleDelete = async (materialId: number) => {
    try {
      await deleteInteractiveMaterial.mutateAsync({ id: materialId });
      addToast({
        title: t("interactive_material.deleted"),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      addToast({
        title: t("interactive_material.deleteError"),
        description: t("interactive_material.deleteErrorDescription"),
        variant: "error",
      });
    }
  };

  const { trackEvent: track } = trackEvent();
  const handleClick = () => {
    track(
      {
        category: "ContentGeneration",
        action: "interactive.material.generate",
        label: "InteractiveMaterial generation click",
        nonInteraction: false,
      },
      {
        location: "/interactive-materials/generate",
        element: "generate-button",
      },
    );
  };

  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <HeroTitle
          onClick={handleClick}
          title={t("quickActions.interactiveMaterial")}
          description={t("quickActions.interactiveMaterialDesc")}
          titleStyle="bg-gradient-to-r from-rose-600 to-pink-600 dark:from-rose-400 dark:to-pink-400"
          titleBackgroundStyle="bg-gradient-to-r from-rose-50/30 to-pink-50/30 dark:from-rose-900/30 dark:to-pink-900/30"
          buttonStyle="bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700"
          buttonLink="/content/interactive-materials/create"
        />

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <div className="w-16 h-16 border-4 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
            <Text
              variant={"span"}
              className="!text-gray-600 dark:!text-gray-300"
              weight={"medium"}
              size={"lg"}
            >
              interactive_material.loading.title
            </Text>
          </div>
        )}

        {/* Error State */}
        {error && (
          <ErrorComponent
            title={t("interactive_material.error.title")}
            refetch={refetch}
            description={t("interactive_material.error.description")}
            retryButton={t("interactive_material.error.retryButton")}
          />
        )}

        {/* Materials Grid */}
        {!isLoading && !error && materials?.data && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {materials.data.map((material: InteractiveMaterialResponseDto) => (
              <Card
                key={material.id}
                item={{
                  id: material.id?.toString() ?? "",
                  imageUrl: material.youtubeThumbnailUrl ?? "",
                  prompt: material.title ?? material.youtubeTitle ?? "",
                  description:
                    material.description ?? material.youtubeDescription ?? "",
                  courseName: "",
                  gradeLevel: 0,
                  createdAt: material.createdAt ?? "",
                }}
                selectItem={() =>
                  navigate(`/content/interactive-materials/${material.id}`)
                }
                setShowDeletePopup={(id) => setShowDeletePopup(Number(id))}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading &&
          !error &&
          (!materials?.data || materials.data.length === 0) && (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4 bg-white dark:bg-dark-600 rounded-2xl p-8">
              <div className="p-4 rounded-full bg-gray-100 dark:bg-gray-800">
                <Package
                  size={32}
                  className="text-gray-600 dark:text-gray-400"
                />
              </div>
              <Text variant={"h2"}>interactive_material.empty.title</Text>
              <Text
                variant={"p"}
                status="subtle"
                size={"base"}
                className="text-center"
              >
                interactive_material.empty.description
              </Text>
              <Button
                variant="solid"
                size="md"
                rounded="lg"
                bgColor="bg-rose-500 hover:bg-rose-600"
                textColor="text-white"
                onClick={() =>
                  navigate("/content/interactive-materials/create")
                }
              >
                {t("interactive_material.empty.createButton")}
              </Button>
            </div>
          )}

        <ToolHelper
          toolPath={"interactive-materials"}
          shouldShowTour={materials?.data && materials?.data?.length > 0}
          toolType={t(`sidebar.menu.toolbox.interactiveMaterials`)}
        />

        {/* Delete Confirmation Popup */}
        {showDeletePopup && (
          <DeleteConfirmPopUp
            title={t("interactive_material.confirmDelete", {
              title: materials?.data?.find(
                (material) => material.id === showDeletePopup,
              )?.title,
            })}
            showDeletePopup={showDeletePopup}
            handleDelete={handleDelete}
            setShowDeletePopup={setShowDeletePopup}
          />
        )}
      </div>
    </div>
  );
};

export default InteractiveMaterialsView;
